import { useEffect, useState } from "react";
import { Mail, Phone, MapPin, Send, Download } from "lucide-react";
import { toast } from "sonner";
import { MaskLine, Reveal } from "@/components/Reveal";
import { api, resolveImg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const DEFAULT_QUESTIONNAIRE = "/assets/inplant-questionnaire.pdf";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [questionnaireUrl, setQuestionnaireUrl] = useState(DEFAULT_QUESTIONNAIRE);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/content");
        const url = data?.data?.questionnaire_url;
        if (url && url.trim()) setQuestionnaireUrl(url);
      } catch { /* fall back to default */ }
    })();
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in your name, email, phone and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you — your enquiry has been sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-[#eaf6fc] pt-40 pb-24 relative overflow-hidden border-b border-black/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 relative z-10">
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#3da8d8] mb-8">Get in Touch</p>
          <h1 className="font-display font-bold text-[#334155] text-5xl sm:text-7xl lg:text-8xl tracking-tight leading-[0.98]">
            <MaskLine delay={0.1}>Let&apos;s talk about</MaskLine>
            <MaskLine delay={0.25}>your <span className="text-[#3da8d8]">project.</span></MaskLine>
          </h1>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-16">
          {/* Info */}
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-display text-3xl lg:text-4xl tracking-tight mb-10">Reach our team directly.</h2>
            </Reveal>
            <div className="space-y-8">
              <Reveal delay={0.05}>
                <a href="mailto:sales@manikindia.com" data-testid="contact-email" className="group flex items-start gap-4">
                  <span className="w-11 h-11 flex items-center justify-center bg-[#3da8d8]/10 shrink-0"><Mail className="text-[#3da8d8]" size={20} /></span>
                  <div>
                    <p className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Email</p>
                    <p className="text-lg group-hover:text-[#3da8d8] transition-colors">sales@manikindia.com</p>
                  </div>
                </a>
              </Reveal>
              <Reveal delay={0.1}>
                <div data-testid="contact-phone" className="flex items-start gap-4">
                  <span className="w-11 h-11 flex items-center justify-center bg-[#78be27]/10 shrink-0"><Phone className="text-[#78be27]" size={20} /></span>
                  <div>
                    <p className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Phone</p>
                    <a href="tel:+918424040899" className="block text-lg hover:text-[#78be27] transition-colors">+91 84240 40899</a>
                    <a href="tel:+919845172851" data-testid="contact-phone-secondary" className="block text-lg hover:text-[#78be27] transition-colors">+91 98451 72851</a>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="flex items-start gap-4">
                  <span className="w-11 h-11 flex items-center justify-center bg-[#3da8d8]/10 shrink-0"><MapPin className="text-[#3da8d8]" size={20} /></span>
                  <div>
                    <p className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B] mb-2">Corporate Office</p>
                    <p className="leading-relaxed">Ground &amp; Mezz. Floor, Lakhi Industry House, LBS Marg, Bhandup (W), Mumbai - 400078</p>
                    <p className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B] mb-2 mt-6">Factory</p>
                    <p className="leading-relaxed">A-805/1, Sector 2, Kopar Khairane, Navi Mumbai, Maharashtra 400710</p>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal>
              <form onSubmit={submit} data-testid="contact-form" className="border border-black/10 bg-white p-8 lg:p-12">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Name *</Label>
                    <Input data-testid="contact-name-input" value={form.name} onChange={update("name")} className="mt-2 rounded-none border-black/15 h-12" placeholder="Your name" />
                  </div>
                  <div>
                    <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Email *</Label>
                    <Input data-testid="contact-email-input" type="email" value={form.email} onChange={update("email")} className="mt-2 rounded-none border-black/15 h-12" placeholder="you@company.com" />
                  </div>
                  <div>
                    <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Company</Label>
                    <Input data-testid="contact-company-input" value={form.company} onChange={update("company")} className="mt-2 rounded-none border-black/15 h-12" placeholder="Company name" />
                  </div>
                  <div>
                    <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Phone *</Label>
                    <Input data-testid="contact-phone-input" value={form.phone} onChange={update("phone")} className="mt-2 rounded-none border-black/15 h-12" placeholder="+91" />
                  </div>
                </div>
                <div className="mt-6">
                  <Label className="font-mono-accent text-[10px] uppercase tracking-widest text-[#52525B]">Message *</Label>
                  <Textarea data-testid="contact-message-input" value={form.message} onChange={update("message")} className="mt-2 rounded-none border-black/15 min-h-[140px]" placeholder="Tell us about your requirement…" />
                </div>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                    className="btn-fill group inline-flex items-center gap-3 border border-[#3da8d8] px-10 py-4 font-mono-accent text-xs uppercase tracking-widest text-[#3da8d8] hover:text-white disabled:opacity-50"
                  >
                    {loading ? "Sending…" : "Send Enquiry"}
                    <Send size={15} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                  <a
                    href={resolveImg(questionnaireUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-download-questionnaire"
                    className="group inline-flex items-center gap-3 border border-[#78be27] px-8 py-4 font-mono-accent text-xs uppercase tracking-widest text-[#78be27] hover:bg-[#78be27] hover:text-white transition-colors"
                  >
                    Download Questionnaire
                    <Download size={14} className="group-hover:translate-y-0.5 transition-transform" />
                  </a>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MAPS */}
      <section className="border-t border-black/10 bg-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20">
          <Reveal>
            <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#3da8d8] mb-4">Find us on the map</p>
            <h2 className="font-display text-3xl lg:text-5xl tracking-tight text-[#334155] mb-12">Two locations, one team.</h2>
          </Reveal>
          <div className="grid lg:grid-cols-2 gap-8">
            <Reveal>
              <div data-testid="map-corporate" className="border border-black/10 bg-white">
                <div className="px-6 py-5 border-b border-black/10 flex items-start justify-between gap-4">
                  <div>
                    <p className="font-mono-accent text-[10px] uppercase tracking-widest text-[#3da8d8] mb-2">Corporate Office</p>
                    <p className="font-display text-lg leading-snug text-[#334155]">Lakhi Industry House, Bhandup (W), Mumbai</p>
                  </div>
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=Lakhi+Industry+House+LBS+Marg+Bhandup+West+Mumbai+400078"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="map-corporate-directions"
                    className="shrink-0 font-mono-accent text-[10px] uppercase tracking-widest text-[#334155] hover:text-[#3da8d8] border border-black/15 px-3 py-2 hover:border-[#3da8d8]"
                  >
                    Directions
                  </a>
                </div>
                <iframe
                  title="Manik Engineering — Corporate Office, Bhandup"
                  src="https://www.google.com/maps?q=Lakhi+Industry+House+LBS+Marg+Bhandup+West+Mumbai+400078&output=embed"
                  width="100%"
                  height="340"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div data-testid="map-factory" className="border border-black/10 bg-white">
                <div className="px-6 py-5 border-b border-black/10 flex items-start justify-between gap-4">
                  <div>
                    <p className="font-mono-accent text-[10px] uppercase tracking-widest text-[#78be27] mb-2">Factory</p>
                    <p className="font-display text-lg leading-snug text-[#334155]">A-805/1, Sector 2, Kopar Khairane, Navi Mumbai, Maharashtra 400710</p>
                  </div>
                  <a
                    href="https://maps.app.goo.gl/SabGzj4n6gyBUv6u7"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="map-factory-directions"
                    className="shrink-0 font-mono-accent text-[10px] uppercase tracking-widest text-[#334155] hover:text-[#78be27] border border-black/15 px-3 py-2 hover:border-[#78be27]"
                  >
                    Directions
                  </a>
                </div>
                <iframe
                  title="Manik Engineering — Factory, Kopar Khairane Navi Mumbai"
                  src="https://www.google.com/maps?q=19.098431,73.021074&z=17&output=embed"
                  width="100%"
                  height="340"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
